import React from "react";
import {
  AppBar,
  Avatar,
  Box,
  Button,
  Container,
  Divider,
  Drawer,
  IconButton,
  Stack,
  Toolbar,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import LoginRoundedIcon from "@mui/icons-material/LoginRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import DashboardRoundedIcon from "@mui/icons-material/DashboardRounded";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import { Link as RouterLink, useNavigate } from "react-router-dom";

import { useAuth } from "../context/AuthContext";
import logo from "../images/Recurso 7.svg";

const BRAND = {
  orange: "#F35A3A",
  orangeDark: "#DF4728",
  dark: "#19242F",
};

function useScrolled(threshold = 8) {
  const [scrolled, setScrolled] = React.useState(false);

  React.useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > threshold);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [threshold]);

  return scrolled;
}

function scrollHomeSection(navigate, id) {
  const scroll = () =>
    document.getElementById(id)?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });

  const route = window.location.hash.slice(1).split("?")[0];
  if (route === "/" || route === "") {
    scroll();
    return;
  }

  navigate("/");
  window.setTimeout(scroll, 100);
}

function getInitials(user) {
  const source = String(user?.name || user?.email || "WP").trim();
  const parts = source.split(/\s+/).filter(Boolean);

  return parts
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join("");
}

export default function Navbar() {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const navigate = useNavigate();
  const { user, loading, logout } = useAuth();
  const scrolled = useScrolled();
  const [open, setOpen] = React.useState(false);
  const [loggingOut, setLoggingOut] = React.useState(false);

  const closeAndNavigate = (path) => {
    setOpen(false);
    navigate(path);
  };

  const handleLogout = async () => {
    try {
      setLoggingOut(true);
      await logout();
      setOpen(false);
      navigate("/", { replace: true });
    } finally {
      setLoggingOut(false);
    }
  };

  const links = [
    {
      label: "Cómo funciona",
      action: () => scrollHomeSection(navigate, "como-funciona"),
    },
    {
      label: "Herramientas",
      action: () => scrollHomeSection(navigate, "herramientas"),
    },
    { label: "Planes", action: () => navigate("/servicios") },
  ];

  return (
    <>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          bgcolor: scrolled
            ? "rgba(255,255,255,.94)"
            : "rgba(255,255,255,.82)",
          color: BRAND.dark,
          borderBottom: "1px solid rgba(25,36,47,.08)",
          backdropFilter: "blur(18px)",
          boxShadow: scrolled ? "0 12px 35px rgba(25,36,47,.08)" : "none",
          transition: "background-color .25s ease, box-shadow .25s ease",
        }}
      >
        <Container maxWidth="lg">
          <Toolbar disableGutters sx={{ minHeight: { xs: 72, md: 78 } }}>
            <Box
              component={RouterLink}
              to="/"
              aria-label="WebProfit - inicio"
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 1.25,
                color: BRAND.dark,
                textDecoration: "none",
              }}
            >
              <Box
                component="img"
                src={logo}
                alt="WebProfit"
                sx={{ width: 43, height: 43, objectFit: "contain" }}
              />
              <Box>
                <Typography
                  sx={{
                    fontWeight: 950,
                    lineHeight: 1,
                    letterSpacing: "-.035em",
                    fontSize: { xs: "1.25rem", md: "1.45rem" },
                  }}
                >
                  WebProfit
                </Typography>
                <Typography
                  sx={{
                    display: { xs: "none", sm: "block" },
                    mt: 0.35,
                    color: "rgba(25,36,47,.52)",
                    fontWeight: 750,
                    fontSize: ".68rem",
                    letterSpacing: ".08em",
                    textTransform: "uppercase",
                  }}
                >
                  Tu negocio online
                </Typography>
              </Box>
            </Box>

            <Box sx={{ flexGrow: 1 }} />

            {isDesktop ? (
              <Stack direction="row" alignItems="center" spacing={0.5}>
                {links.map((item) => (
                  <Button
                    key={item.label}
                    onClick={item.action}
                    sx={{
                      px: 1.45,
                      color: BRAND.dark,
                      borderRadius: 999,
                      textTransform: "none",
                      fontWeight: 800,
                      "&:hover": { bgcolor: "rgba(25,36,47,.045)" },
                    }}
                  >
                    {item.label}
                  </Button>
                ))}

                {!loading && user ? (
                  <Stack direction="row" alignItems="center" spacing={1.1} sx={{ ml: 1.2 }}>
                    <Stack direction="row" alignItems="center" spacing={0.9}>
                      <Avatar
                        sx={{
                          width: 36,
                          height: 36,
                          bgcolor: "rgba(243,90,58,.12)",
                          color: "#C83E21",
                          fontWeight: 950,
                          fontSize: ".82rem",
                        }}
                      >
                        {getInitials(user)}
                      </Avatar>
                      <Box sx={{ maxWidth: 150 }}>
                        <Typography
                          noWrap
                          sx={{
                            color: BRAND.dark,
                            fontWeight: 900,
                            fontSize: ".82rem",
                            lineHeight: 1.1,
                          }}
                        >
                          {user.name || "Mi cuenta"}
                        </Typography>
                        <Typography
                          noWrap
                          sx={{
                            mt: 0.25,
                            color: "rgba(25,36,47,.5)",
                            fontWeight: 700,
                            fontSize: ".68rem",
                          }}
                        >
                          Sesión activa
                        </Typography>
                      </Box>
                    </Stack>

                    <Button
                      onClick={() => navigate("/dashboard")}
                      variant="contained"
                      startIcon={<DashboardRoundedIcon />}
                      sx={{
                        minHeight: 44,
                        px: 2.2,
                        borderRadius: 999,
                        bgcolor: BRAND.orange,
                        textTransform: "none",
                        fontWeight: 900,
                        boxShadow: "0 10px 24px rgba(243,90,58,.24)",
                        "&:hover": {
                          bgcolor: BRAND.orangeDark,
                          transform: "translateY(-1px)",
                        },
                      }}
                    >
                      Ir a mi panel
                    </Button>
                  </Stack>
                ) : (
                  <>
                    <Button
                      onClick={() => navigate("/login")}
                      startIcon={<LoginRoundedIcon />}
                      disabled={loading}
                      sx={{
                        ml: 1,
                        color: BRAND.dark,
                        borderRadius: 999,
                        textTransform: "none",
                        fontWeight: 850,
                      }}
                    >
                      Ingresar
                    </Button>
                    <Button
                      onClick={() => navigate("/registro")}
                      variant="contained"
                      endIcon={<ArrowForwardRoundedIcon />}
                      disabled={loading}
                      sx={{
                        ml: 1,
                        minHeight: 44,
                        px: 2.4,
                        borderRadius: 999,
                        bgcolor: BRAND.orange,
                        textTransform: "none",
                        fontWeight: 900,
                        boxShadow: "0 10px 24px rgba(243,90,58,.24)",
                        "&:hover": {
                          bgcolor: BRAND.orangeDark,
                          transform: "translateY(-1px)",
                        },
                      }}
                    >
                      Crear página gratis
                    </Button>
                  </>
                )}
              </Stack>
            ) : (
              <IconButton
                aria-label="Abrir menú"
                onClick={() => setOpen(true)}
                sx={{
                  color: BRAND.dark,
                  border: "1px solid rgba(25,36,47,.11)",
                  borderRadius: 2.5,
                }}
              >
                <MenuRoundedIcon />
              </IconButton>
            )}
          </Toolbar>
        </Container>
      </AppBar>

      <Drawer
        anchor="right"
        open={open}
        onClose={() => setOpen(false)}
        PaperProps={{
          sx: {
            width: "min(88vw, 390px)",
            bgcolor: "#fff",
            color: BRAND.dark,
            p: 2.5,
          },
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" alignItems="center" spacing={1.1}>
            <Box component="img" src={logo} alt="WebProfit" sx={{ width: 38, height: 38 }} />
            <Typography sx={{ fontWeight: 950, fontSize: "1.3rem" }}>
              WebProfit
            </Typography>
          </Stack>
          <IconButton aria-label="Cerrar menú" onClick={() => setOpen(false)}>
            <CloseRoundedIcon />
          </IconButton>
        </Stack>

        <Divider sx={{ my: 2.5 }} />

        <Stack spacing={0.7}>
          <Button
            onClick={() => closeAndNavigate("/")}
            sx={{
              justifyContent: "flex-start",
              color: BRAND.dark,
              textTransform: "none",
              fontWeight: 850,
              py: 1.25,
            }}
          >
            Inicio
          </Button>
          {links.map((item) => (
            <Button
              key={item.label}
              onClick={() => {
                setOpen(false);
                item.action();
              }}
              sx={{
                justifyContent: "flex-start",
                color: BRAND.dark,
                textTransform: "none",
                fontWeight: 850,
                py: 1.25,
              }}
            >
              {item.label}
            </Button>
          ))}
        </Stack>

        {!loading && user ? (
          <Box
            sx={{
              mt: 3,
              p: 2.2,
              borderRadius: 4,
              bgcolor: "#F3F8F5",
              border: "1px solid rgba(42,157,103,.16)",
            }}
          >
            <Stack direction="row" spacing={1.2} alignItems="center">
              <Avatar
                sx={{
                  width: 42,
                  height: 42,
                  bgcolor: "#2A9D67",
                  fontWeight: 950,
                  fontSize: ".88rem",
                }}
              >
                {getInitials(user)}
              </Avatar>
              <Box sx={{ minWidth: 0 }}>
                <Typography noWrap sx={{ color: BRAND.dark, fontWeight: 900 }}>
                  {user.name || "Mi cuenta"}
                </Typography>
                <Typography
                  noWrap
                  sx={{ color: "rgba(25,36,47,.58)", fontSize: ".82rem" }}
                >
                  {user.email || "Sesión activa"}
                </Typography>
              </Box>
            </Stack>
            <Stack direction="row" spacing={0.8} alignItems="center" sx={{ mt: 1.5 }}>
              <CheckCircleRoundedIcon sx={{ color: "#2A9D67", fontSize: 19 }} />
              <Typography sx={{ color: "#247A55", fontWeight: 850, fontSize: ".86rem" }}>
                Tu sesión continúa activa en la home.
              </Typography>
            </Stack>
          </Box>
        ) : (
          <Box
            sx={{
              mt: 3,
              p: 2.2,
              borderRadius: 4,
              bgcolor: "#FFF4EF",
              border: "1px solid rgba(243,90,58,.14)",
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <CheckCircleRoundedIcon sx={{ color: BRAND.orange }} />
              <Typography sx={{ color: "#C83E21", fontWeight: 900 }}>
                Empezá gratis
              </Typography>
            </Stack>
            <Typography
              sx={{
                mt: 1,
                color: "rgba(25,36,47,.64)",
                fontSize: ".9rem",
                lineHeight: 1.55,
              }}
            >
              Creá tu cuenta, confirmá tu email y armá tu primera página paso a paso.
            </Typography>
          </Box>
        )}

        <Stack spacing={1.2} sx={{ mt: "auto", pt: 4 }}>
          {!loading && user ? (
            <>
              <Button
                onClick={() => closeAndNavigate("/dashboard")}
                variant="contained"
                startIcon={<DashboardRoundedIcon />}
                sx={{
                  minHeight: 52,
                  borderRadius: 999,
                  bgcolor: BRAND.orange,
                  textTransform: "none",
                  fontWeight: 900,
                  "&:hover": { bgcolor: BRAND.orangeDark },
                }}
              >
                Continuar en mi panel
              </Button>
              <Button
                onClick={handleLogout}
                disabled={loggingOut}
                startIcon={<LogoutRoundedIcon />}
                sx={{
                  minHeight: 48,
                  borderRadius: 999,
                  color: "rgba(25,36,47,.66)",
                  textTransform: "none",
                  fontWeight: 800,
                }}
              >
                {loggingOut ? "Cerrando sesión..." : "Cerrar sesión"}
              </Button>
            </>
          ) : (
            <>
              <Button
                onClick={() => closeAndNavigate("/registro")}
                variant="contained"
                endIcon={<ArrowForwardRoundedIcon />}
                disabled={loading}
                sx={{
                  minHeight: 52,
                  borderRadius: 999,
                  bgcolor: BRAND.orange,
                  textTransform: "none",
                  fontWeight: 900,
                  "&:hover": { bgcolor: BRAND.orangeDark },
                }}
              >
                Crear mi página gratis
              </Button>
              <Button
                onClick={() => closeAndNavigate("/login")}
                variant="outlined"
                startIcon={<LoginRoundedIcon />}
                disabled={loading}
                sx={{
                  minHeight: 50,
                  borderRadius: 999,
                  color: BRAND.dark,
                  borderColor: "rgba(25,36,47,.2)",
                  textTransform: "none",
                  fontWeight: 850,
                }}
              >
                Ya tengo una cuenta
              </Button>
            </>
          )}
        </Stack>
      </Drawer>
    </>
  );
}
